import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { videoAPI, presetAPI, generateAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  FileText, Save, Loader2, ArrowRight, Video, Sparkles, AlertCircle,
  RotateCcw, Clock, Download, FileDown, Copy, Check, Hash, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

function formatTimecode(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function VideoDetailPage() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edited, setEdited] = useState(false);
  const [showTimecodes, setShowTimecodes] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generations, setGenerations] = useState([]);

  useEffect(() => {
    loadData();
  }, [videoId]);

  const loadData = async () => {
    try {
      const [videoRes, presetsRes] = await Promise.all([
        videoAPI.get(videoId),
        presetAPI.list(),
      ]);
      setVideo(videoRes.data);
      setTranscript(videoRes.data.transcript || '');
      setPresets(presetsRes.data);
      // Load generations if video is ready
      if (videoRes.data.status === 'ready') {
        try {
          const gensRes = await generateAPI.listByVideo(videoId);
          setGenerations(gensRes.data);
        } catch {}
      }
    } catch (err) {
      toast.error('Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const segments = useMemo(() => video?.transcript_segments || [], [video?.transcript_segments]);

  const timecodedText = useMemo(() => {
    if (!segments.length) return transcript;
    return segments.map(seg => `[${formatTimecode(seg.start)}] ${seg.text}`).join('\n\n');
  }, [segments, transcript]);

  const displayText = useMemo(() => {
    if (isEditing) return transcript;
    if (showTimecodes && segments.length) return timecodedText;
    return transcript;
  }, [isEditing, showTimecodes, segments, timecodedText, transcript]);

  const handleSaveTranscript = async () => {
    setSaving(true);
    try {
      await videoAPI.updateTranscript(videoId, transcript);
      setEdited(false);
      setIsEditing(false);
      toast.success('Transcript saved');
    } catch (err) {
      toast.error('Failed to save transcript');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyTranscript = async () => {
    const text = showTimecodes && segments.length ? timecodedText : transcript;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Transcript copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const blob = await videoAPI.downloadTranscript(videoId, format, showTimecodes);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const baseName = video.filename.replace(/\.[^.]+$/, '');
      const ext = format === 'docx' ? 'docx' : format === 'pdf' ? 'pdf' : 'txt';
      a.download = `${baseName}_transcript.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Transcript downloaded as .${ext}`);
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const handleRetry = async () => {
    try {
      await videoAPI.retry(videoId);
      toast.success('Retry started');
      setVideo({ ...video, status: 'processing' });
      const poll = setInterval(async () => {
        const res = await videoAPI.get(videoId);
        if (res.data.status === 'ready') {
          clearInterval(poll);
          setVideo(res.data);
          setTranscript(res.data.transcript || '');
        } else if (res.data.status === 'failed') {
          clearInterval(poll);
          setVideo(res.data);
        }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!video) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-testid="video-not-found">
        <AlertCircle className="w-12 h-12 text-muted-foreground/40 mb-4" />
        <p className="text-lg font-medium">Video not found</p>
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mt-4">Go to Dashboard</Button>
      </div>
    );
  }

  const isProcessing = ['processing', 'extracting_audio', 'transcribing'].includes(video.status);

  return (
    <div className="p-8 space-y-8" data-testid="video-detail-page">
      {/* Video Info Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-secondary flex items-center justify-center">
            <Video className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{video.filename}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {(video.file_size / (1024 * 1024)).toFixed(1)} MB &middot; Uploaded {new Date(video.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={`${
            video.status === 'ready' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
            video.status === 'failed' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
            'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
          }`}
          data-testid="video-status-badge"
        >
          {video.status === 'ready' ? 'Ready' : video.status === 'failed' ? 'Failed' : 'Processing'}
        </Badge>
      </div>

      {/* Processing State */}
      {isProcessing && (
        <Card className="bg-zinc-900/60 border-yellow-500/20" data-testid="video-processing-state">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-10 h-10 animate-spin text-yellow-400 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-semibold">Processing Video</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {video.status === 'extracting_audio' ? 'Extracting audio from video...' :
               video.status === 'transcribing' ? 'Generating transcript...' :
               'Processing...'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Failed State */}
      {video.status === 'failed' && (
        <Card className="bg-zinc-900/60 border-destructive/20" data-testid="video-failed-state">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-4" />
            <h3 className="font-heading text-lg font-semibold">Transcription Failed</h3>
            <p className="text-sm text-muted-foreground mt-1">{video.error || 'An error occurred during processing.'}</p>
            <Button onClick={handleRetry} className="mt-4 rounded-full" variant="secondary" data-testid="retry-button">
              <RotateCcw className="w-4 h-4 mr-2" /> Retry Transcription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Transcript + Presets (Split layout when ready) */}
      {video.status === 'ready' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Transcript Panel */}
          <Card className="bg-zinc-900/60 border-white/5" data-testid="transcript-panel">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  Transcript
                </CardTitle>
                <div className="flex items-center gap-2">
                  {edited && (
                    <Badge variant="outline" className="text-[10px] bg-yellow-500/10 text-yellow-400 border-yellow-500/20">Unsaved</Badge>
                  )}
                  <Button
                    onClick={handleSaveTranscript}
                    disabled={saving || !edited}
                    size="sm"
                    variant="secondary"
                    className="rounded-full"
                    data-testid="save-transcript-button"
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                    Save
                  </Button>
                </div>
              </div>

              {/* Controls row */}
              <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-white/5">
                {/* Timecode toggle */}
                <div className="flex items-center gap-2" data-testid="timecode-toggle-container">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                  <label htmlFor="timecode-toggle" className="text-xs text-muted-foreground font-medium cursor-pointer select-none">
                    Timecodes
                  </label>
                  <Switch
                    id="timecode-toggle"
                    checked={showTimecodes}
                    onCheckedChange={(v) => { setShowTimecodes(v); if (isEditing) setIsEditing(false); }}
                    disabled={!segments.length}
                    data-testid="timecode-toggle"
                  />
                </div>

                {/* Edit toggle */}
                <Button
                  variant={isEditing ? "default" : "ghost"}
                  size="sm"
                  className={`h-7 text-xs rounded-full ${isEditing ? 'bg-primary/20 text-primary' : ''}`}
                  onClick={() => { setIsEditing(!isEditing); if (!isEditing) setShowTimecodes(false); }}
                  data-testid="edit-transcript-toggle"
                >
                  {isEditing ? 'Editing' : 'Edit'}
                </Button>

                <div className="flex items-center gap-1 ml-auto">
                  {/* Copy button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleCopyTranscript}
                    data-testid="copy-transcript-button"
                  >
                    {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>

                  {/* Download dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="download-transcript-trigger">
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-border/50" data-testid="download-dropdown">
                      <DropdownMenuItem
                        onClick={() => handleDownload('txt')}
                        disabled={!!downloading}
                        className="cursor-pointer"
                        data-testid="download-txt"
                      >
                        <FileDown className="w-4 h-4 mr-2" />
                        {downloading === 'txt' ? 'Downloading...' : 'Text File (.txt)'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownload('docx')}
                        disabled={!!downloading}
                        className="cursor-pointer"
                        data-testid="download-docx"
                      >
                        <FileDown className="w-4 h-4 mr-2" />
                        {downloading === 'docx' ? 'Downloading...' : 'Word Document (.docx)'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownload('pdf')}
                        disabled={!!downloading}
                        className="cursor-pointer"
                        data-testid="download-pdf"
                      >
                        <FileDown className="w-4 h-4 mr-2" />
                        {downloading === 'pdf' ? 'Downloading...' : 'PDF Document (.pdf)'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Timecodes info */}
              {showTimecodes && segments.length > 0 && !isEditing && (
                <p className="text-[10px] text-muted-foreground/60 mt-2">
                  {segments.length} segments &middot; Toggle off timecodes to copy clean text
                </p>
              )}
              {isEditing && (
                <p className="text-[10px] text-yellow-400/60 mt-2">
                  Editing plain transcript. Timecodes are preserved separately.
                </p>
              )}
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[380px]">
                {isEditing ? (
                  <Textarea
                    value={transcript}
                    onChange={(e) => { setTranscript(e.target.value); setEdited(true); }}
                    className="min-h-[360px] bg-secondary/30 border-white/5 focus:border-primary resize-none font-sans text-sm leading-relaxed"
                    placeholder="Transcript will appear here..."
                    data-testid="transcript-textarea"
                  />
                ) : (
                  <div className="space-y-0 pr-4" data-testid="transcript-display">
                    {showTimecodes && segments.length > 0 ? (
                      segments.map((seg, i) => (
                        <div key={i} className="flex gap-3 py-2 group" data-testid={`segment-${i}`}>
                          <span className="text-[10px] font-mono text-primary/60 bg-primary/5 px-1.5 py-0.5 rounded h-fit shrink-0 mt-0.5 group-hover:text-primary/90 transition-colors">
                            {formatTimecode(seg.start)}
                          </span>
                          <p className="text-sm leading-relaxed text-foreground/90">{seg.text}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap" data-testid="plain-transcript">
                        {transcript}
                      </p>
                    )}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Preset Selection */}
          <Card className="bg-zinc-900/60 border-white/5" data-testid="preset-selection-panel">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-accent" strokeWidth={1.5} />
                Select a Preset
              </CardTitle>
            </CardHeader>
            <CardContent>
              {presets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="no-presets">
                  <Sparkles className="w-10 h-10 text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">No presets available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {presets.map((preset) => (
                    <div
                      key={preset.id}
                      className="p-5 rounded-xl bg-secondary/30 border border-white/5 hover:border-primary/30 transition-all duration-300 cursor-pointer group hover:-translate-y-0.5"
                      onClick={() => navigate(`/generate/${videoId}/${preset.id}`)}
                      data-testid={`preset-card-${preset.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-heading font-semibold text-sm">{preset.name}</h4>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{preset.description}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {preset.targetPlatforms?.map((p, i) => (
                          <Badge key={i} variant="secondary" className="text-[10px] bg-indigo-500/10 text-indigo-400 border-0">{p}</Badge>
                        ))}
                        <Badge variant="secondary" className="text-[10px] bg-purple-500/10 text-purple-400 border-0">{preset.tone}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Previous Generations */}
      {video.status === 'ready' && generations.length > 0 && (
        <Card className="bg-zinc-900/60 border-white/5" data-testid="previous-generations-section">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
              Previous Generations
              <Badge variant="secondary" className="text-[10px] ml-1">{generations.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="generations-list">
              {generations.map((gen) => (
                <div
                  key={gen.id}
                  className="group flex items-start gap-4 p-4 rounded-xl bg-secondary/20 border border-white/5 hover:border-primary/20 transition-all cursor-pointer hover:-translate-y-0.5"
                  onClick={() => navigate(`/generate/${videoId}/${gen.preset_id}`)}
                  data-testid={`generation-item-${gen.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold text-sm truncate">{gen.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{gen.description}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="secondary" className="text-[10px] bg-indigo-500/10 text-indigo-400 border-0">{gen.preset_name}</Badge>
                      {gen.hashtags?.slice(0, 3).map((h, j) => (
                        <span key={j} className="text-[10px] text-muted-foreground/60">#{h.replace('#', '')}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(gen.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
